/**
 * @fileoverview Step Save Hook - Unified save logic for ingestion steps.
 *
 * Provides a consistent API for step components to save data and update
 * audit record section statuses in a single operation.
 *
 * @example
 * const { saveStep, isSaving } = useStepSave();
 *
 * const onSubmit = async (data) => {
 *   const success = await saveStep(
 *     async () => await createPoliceReport({ matterId, data }).unwrap(),
 *     { police_report: 'found' },
 *     { successMessage: 'Police report saved' }
 *   );
 *   onSaveComplete?.(success);
 * };
 */
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

import type {
  IngestionSection,
  IngestionSectionStatus,
} from '@/services/types/case-ingestion-types';

import { useUnifiedFlow } from '../context/UnifiedFlowContext';

interface SaveStepOptions {
  successMessage?: string;
  errorMessage?: string;
}

/**
 * Hook for ingestion step save logic.
 *
 * Provides:
 * - `isSaving` — loading state for save operations
 * - `saveStep` — wraps domain API call + section status update
 *
 * Section status updates are done via context's `updateSectionStatuses` (batch).
 */
export function useStepSave() {
  const { updateSectionStatuses } = useUnifiedFlow();
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  /**
   * Save step data: runs domain API call, then updates section statuses.
   * Returns true on success, false on error.
   *
   * @param domainApiCall - The domain API mutation to run (e.g., create coverage)
   * @param sectionUpdates - Map of section IDs to their new status
   * @param options - Toast messages
   */
  const saveStep = useCallback(
    async (
      domainApiCall: () => Promise<void>,
      sectionUpdates: Partial<Record<IngestionSection, IngestionSectionStatus | null>>,
      options?: SaveStepOptions,
    ): Promise<boolean> => {
      if (isSavingRef.current) return false;
      isSavingRef.current = true;
      setIsSaving(true);
      try {
        await domainApiCall();
        await updateSectionStatuses(sectionUpdates);

        toast.success(options?.successMessage || 'Saved successfully');
        return true;
      } catch {
        toast.error(options?.errorMessage || 'Failed to save');
        return false;
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
      }
    },
    [updateSectionStatuses],
  );

  /**
   * Mark a single section as found or not_found.
   * Useful for simple toggle operations.
   */
  const markSection = useCallback(
    async (
      section: IngestionSection,
      status: IngestionSectionStatus,
      options?: SaveStepOptions,
    ): Promise<boolean> => {
      if (isSavingRef.current) return false;
      isSavingRef.current = true;
      setIsSaving(true);
      try {
        await updateSectionStatuses({ [section]: status });
        toast.success(
          options?.successMessage ||
            (status === 'not_found' ? 'Marked as not found' : 'Marked as found'),
        );
        return true;
      } catch {
        toast.error(options?.errorMessage || 'Failed to update status');
        return false;
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
      }
    },
    [updateSectionStatuses],
  );

  return {
    isSaving,
    saveStep,
    markSection,
  };
}
